import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { keepPreviousData } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'

import { BanknotesIcon } from '@gto/icons-react/outline/banknotes'
import { ClockIcon } from '@gto/icons-react/outline/clock'
import { CurrencyDollarIcon } from '@gto/icons-react/outline/currency-dollar'
import { LockClosedIcon } from '@gto/icons-react/outline/lock-closed'
import { Button } from '@gto/ui/button'
import { cn } from '@gto/ui/cn'
import { HelpIconTooltip } from '@gto/ui/components/help-icon-tooltip'
import type { PaginationState, SortingState } from '@gto/ui/data-table'
import { Switch } from '@gto/ui/switch'
import { Typography } from '@gto/ui/typography'

import { useGetAdminLedger, useGetProgramStatsView } from '@/api/gen'
import { DashboardKpiTile } from '@/components/dashboard-kpi-tile'
import { DataTable } from '@/components/data-table'
import { EmptyState } from '@/components/empty-state'
import { SelectField } from '@/components/form'
import { Page } from '@/components/page'
import { StatsCard } from '@/components/stats-card'
import { METHOD_LABELS, PAYOUT_METHODS } from '@/domain/payout-methods'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import {
  LEDGER_READINESS_STATUSES,
  LEDGER_SORT_FIELDS,
  type LedgerReadinessStatus,
  type LedgerSearch,
  type LedgerSortField,
  toGetAdminLedgerParams,
} from '@/utils/ledger-search-params'
import { toAmount } from '@/utils/to-amount'

import { useLedgerColumns } from './use-ledger-columns'
import { useReportMissingLedgerMoney } from './use-report-missing-ledger-money'

const DEFAULT_PAGE_SIZE = 25

// The empty-string sentinel `<TextField select>` uses for "no selection"; the
// URL schema uses `undefined`.
const ALL = ''

const READINESS_LABEL_IDS: Record<LedgerReadinessStatus, string> = {
  MISSING_METHOD: 'admin.ledger.readiness.missingMethod',
  MISSING_DETAILS: 'admin.ledger.readiness.missingDetails',
  BELOW_MINIMUM: 'admin.ledger.readiness.belowMinimum',
  PENDING_PAYOUT: 'admin.ledger.readiness.pendingPayout',
  NO_PAYOUT_REQUESTED: 'admin.ledger.readiness.noPayoutRequested',
}

const isSortField = (s: string): s is LedgerSortField =>
  (LEDGER_SORT_FIELDS as readonly string[]).includes(s)
const isReadiness = (s: string): s is LedgerReadinessStatus =>
  (LEDGER_READINESS_STATUSES as readonly string[]).includes(s)
const isMethod = (s: string): s is (typeof PAYOUT_METHODS)[number] =>
  (PAYOUT_METHODS as readonly string[]).includes(s)

const ledgerRoute = getRouteApi('/_private/admin/ledger')

export const LedgerPage = () => {
  const { formatMessage } = useIntl()
  const columns = useLedgerColumns()
  const navigate = ledgerRoute.useNavigate()
  const search = ledgerRoute.useSearch()

  const updateSearch = (delta: Partial<LedgerSearch>, opts?: { resetPage?: boolean }) => {
    void navigate({
      to: '/admin/ledger',
      search: (prev: LedgerSearch) => {
        const next = { ...prev, ...delta }
        if (opts?.resetPage !== false) {
          // A filter change must reset to page 1 — the current index could point
          // past the end of the new result set. Pagination opts out.
          next.page = undefined
        }

        return next
      },
      replace: true,
    })
  }

  const listParams = toGetAdminLedgerParams(search, DEFAULT_PAGE_SIZE)
  const ledgerQuery = useGetAdminLedger(listParams, {
    query: { placeholderData: keepPreviousData },
  })
  const { data, isLoading, isError, refetch } = ledgerQuery
  useReportQueryError(ledgerQuery, 'getAdminLedger failed: ')
  useReportMissingLedgerMoney(data)

  // Program-wide finance snapshot for the KPI strip — same source the payouts
  // page uses, unaffected by the row filters below.
  const statsQuery = useGetProgramStatsView()
  const { data: stats, isLoading: statsLoading } = statsQuery
  useReportQueryError(statsQuery, 'getProgramStatsView failed (ledger): ')

  const rows = data?.rows ?? []
  const rowCount = data?.total ?? 0

  // The BE applies these two as `if payable_only: … elif not include_zero: …`,
  // so include-zero is dead while payable is on. Disable it rather than let the
  // operator flip a switch that silently does nothing.
  const payableOnly = search.payable === 'yes'

  const paginationModel: PaginationState = {
    pageIndex: (search.page ?? 1) - 1,
    pageSize: DEFAULT_PAGE_SIZE,
  }
  const sortModel: SortingState =
    search.sort === undefined ? [] : [{ id: search.sort, desc: (search.dir ?? 'desc') === 'desc' }]

  return (
    <Page>
      <Page.Header title={<FormattedMessage id="admin.ledger.title" />} />

      <Page.Content>
        <div
          className="flex flex-wrap gap-2"
          data-tst="kpi-strip"
        >
          <DashboardKpiTile
            icon={<ClockIcon />}
            loading={statsLoading}
            tooltip={formatMessage({ id: 'tooltip.pendingPayoutsAmount' })}
            label={
              <FormattedMessage
                id="admin.payouts.pendingPayouts.label"
                values={{ count: stats?.pending_payouts_count ?? 0 }}
              />
            }
            value={
              <FormattedNumber
                currency="USD"
                style="currency"
                value={toAmount(stats?.pending_payouts_usd, 'ledger pending_payouts_usd:')}
              />
            }
          />
          <DashboardKpiTile
            icon={<CurrencyDollarIcon />}
            label={<FormattedMessage id="admin.payouts.totalLiability" />}
            loading={statsLoading}
            tooltip={formatMessage({ id: 'tooltip.totalLiability' })}
            value={
              <FormattedNumber
                currency="USD"
                style="currency"
                value={toAmount(stats?.total_open_usd, 'ledger total_open_usd:')}
              />
            }
          />
          <DashboardKpiTile
            icon={<BanknotesIcon />}
            label={<FormattedMessage id="admin.payouts.availableNow" />}
            loading={statsLoading}
            tooltip={formatMessage({ id: 'tooltip.availableNow' })}
            value={
              <FormattedNumber
                currency="USD"
                style="currency"
                value={toAmount(stats?.total_available_usd, 'ledger total_available_usd:')}
              />
            }
          />
          <DashboardKpiTile
            icon={<LockClosedIcon />}
            label={<FormattedMessage id="admin.payouts.locked" />}
            loading={statsLoading}
            tooltip={formatMessage({ id: 'tooltip.lockedBalance' })}
            value={
              <FormattedNumber
                currency="USD"
                style="currency"
                value={toAmount(stats?.total_locked_usd, 'ledger total_locked_usd:')}
              />
            }
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <SelectField
            className="min-w-[190px]"
            label={formatMessage({ id: 'admin.ledger.filter.readiness' })}
            value={search.readiness ?? ALL}
            options={[
              {
                value: ALL,
                label: <FormattedMessage id="admin.ledger.filter.readiness.all" />,
              },
              ...LEDGER_READINESS_STATUSES.map((status) => ({
                value: status,
                label: formatMessage({ id: READINESS_LABEL_IDS[status] }),
              })),
            ]}
            onValueChange={(v) => {
              updateSearch({ readiness: v === ALL || !isReadiness(v) ? undefined : v })
            }}
          />

          <SelectField
            className="min-w-[160px]"
            label={formatMessage({ id: 'admin.ledger.filter.method' })}
            value={search.method ?? ALL}
            options={[
              { value: ALL, label: <FormattedMessage id="admin.ledger.filter.method.all" /> },
              ...PAYOUT_METHODS.map((method) => ({ value: method, label: METHOD_LABELS[method] })),
            ]}
            onValueChange={(v) => {
              updateSearch({ method: v === ALL || !isMethod(v) ? undefined : v })
            }}
          />

          <div className="ml-1 inline-flex items-center gap-1.5">
            <label
              className={cn(
                'inline-flex items-center gap-2',
                payableOnly ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              <Switch
                checked={search.includeZero === 'yes'}
                data-tst="ledger-include-zero"
                disabled={payableOnly}
                onCheckedChange={(checked) =>
                  updateSearch({ includeZero: checked ? 'yes' : undefined })
                }
              />
              <Typography
                color={payableOnly ? 'text.disabled' : 'text.secondary'}
                variant="body2"
              >
                <FormattedMessage id="admin.ledger.filter.includeZero" />
              </Typography>
            </label>
            <HelpIconTooltip
              content={formatMessage({
                id: payableOnly
                  ? 'admin.ledger.filter.includeZero.overridden'
                  : 'admin.ledger.filter.includeZero.tooltip',
              })}
            />
          </div>

          <div className="ml-1 inline-flex items-center gap-1.5">
            <label className="inline-flex cursor-pointer items-center gap-2">
              <Switch
                checked={payableOnly}
                data-tst="ledger-payable-only"
                onCheckedChange={(checked) =>
                  updateSearch({ payable: checked ? 'yes' : undefined })
                }
              />
              <Typography
                color="text.secondary"
                variant="body2"
              >
                <FormattedMessage id="admin.ledger.filter.payable" />
              </Typography>
            </label>
            <HelpIconTooltip
              content={formatMessage({ id: 'admin.ledger.filter.payable.tooltip' })}
            />
          </div>
        </div>

        {isError ? (
          <EmptyState
            description={<FormattedMessage id="admin.ledger.error.description" />}
            title={<FormattedMessage id="admin.ledger.error.title" />}
            action={
              <Button
                color="primary"
                data-tst="ledger-retry"
                variant="outlined"
                onClick={() => void refetch()}
              >
                <FormattedMessage id="admin.ledger.error.retry" />
              </Button>
            }
          />
        ) : !isLoading && rows.length === 0 ? (
          <EmptyState title={<FormattedMessage id="admin.ledger.empty" />} />
        ) : (
          <StatsCard
            icon={<BanknotesIcon />}
            title={<FormattedMessage id="admin.ledger.tableTitle" />}
          >
            <DataTable
              manualPagination
              manualSorting
              columns={columns}
              data={rows}
              getRowId={(row) => row.partner_id}
              loading={isLoading}
              pagination={paginationModel}
              rowCount={rowCount}
              sorting={sortModel}
              onPaginationChange={(next) => {
                updateSearch({ page: next.pageIndex + 1 }, { resetPage: false })
              }}
              onRowClick={(row) => {
                void navigate({
                  to: '/admin/partners/$partnerId',
                  params: { partnerId: row.partner_id },
                })
              }}
              onSortingChange={(next) => {
                const first = next[0]
                if (first === undefined || !isSortField(first.id)) {
                  updateSearch({ sort: undefined, dir: undefined })

                  return
                }
                updateSearch({ sort: first.id, dir: first.desc ? 'desc' : 'asc' })
              }}
            />
          </StatsCard>
        )}
      </Page.Content>
    </Page>
  )
}
